import { c as _c } from "react/compiler-runtime";
import { useUser } from '@clerk/nextjs';
import { useTheme } from 'next-themes';
import { useCallback, useEffect, useRef } from 'react';
export type ThemePreference = 'light' | 'dark' | 'system';
const isThemePreference = (value: unknown): value is ThemePreference => value === 'light' || value === 'dark' || value === 'system';

/**
 * The user's Light / Dark / System choice. next-themes keeps it in the browser;
 * it is also saved on the Clerk user so it follows them to their other devices.
 */
export const useThemePreference = () => {
  const $ = _c(9);
  const {
    theme,
    resolvedTheme,
    setTheme
  } = useTheme();
  const {
    user
  } = useUser();
  let t0;
  if ($[0] !== setTheme || $[1] !== user) {
    t0 = preference => {
      setTheme(preference);
      if (user) {
        user.update({
          unsafeMetadata: {
            ...user.unsafeMetadata,
            theme: preference
          }
        }).catch(_temp);
      }
    };
    $[0] = setTheme;
    $[1] = user;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const setPreference = t0;
  let t1;
  if ($[3] !== theme) {
    t1 = isThemePreference(theme) ? theme : "light";
    $[3] = theme;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = t1 as ThemePreference;
  let t3;
  if ($[5] !== resolvedTheme || $[6] !== setPreference || $[7] !== t2) {
    t3 = {
      preference: t2,
      resolvedTheme,
      setPreference
    };
    $[5] = resolvedTheme;
    $[6] = setPreference;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};

/** Applies the theme saved on the user's account once, when they sign in on a device. */
export const useSyncThemeFromAccount = () => {
  const $ = _c(4);
  const {
    setTheme
  } = useTheme();
  const {
    user
  } = useUser();
  const synced = useRef(false);
  let t0;
  let t1;
  if ($[0] !== setTheme || $[1] !== user) {
    t0 = () => {
      if (!user || synced.current) {
        return;
      }
      synced.current = true;
      const saved = (user.unsafeMetadata as Record<string, unknown>)?.theme;
      if (isThemePreference(saved)) {
        setTheme(saved);
      }
    };
    t1 = [user, setTheme];
    $[0] = setTheme;
    $[1] = user;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
};
function _temp() {}